/**
 * Helpers for social share metadata (Open Graph, Twitter Cards).
 * Best practices: og:title ≤60 chars, og:description 100–160 chars, og:image 1200×630.
 * Share URLs must always use the production site so pasted links work on social.
 */

import { CANONICAL_SITE_ORIGIN, siteOrigin } from '@/lib/site-origin'

const MAX_DESC = 155

/**
 * Base URL for all shared links and canonical URLs (feeds metadataBase in
 * app/layout.tsx). siteOrigin() folds every production host, the Vercel alias
 * included, to https://ryan-realty.com; a preview host is kept; localhost is
 * replaced by production so a shared link always works on social.
 */
export function getCanonicalSiteUrl(): string {
  const origin = siteOrigin()
  return /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?$/i.test(origin) ? CANONICAL_SITE_ORIGIN : origin
}

/** Default hashtags for all Ryan Realty social shares. */
export const DEFAULT_HASHTAGS = '#RyanRealty #BendRealEstate #CentralOregon'

/** Additional hashtags for listing shares (location + real estate). */
export function listingHashtags(city?: string | null): string {
  const parts = ['#RealEstate']
  if (city?.trim()) {
    const c = city.trim().replace(/\s+/g, '')
    parts.push(`#${c.replace(/,/g, '')}OR`)
  }
  return parts.join(' ')
}

/**
 * Trim and clean a string for og:description (100–160 chars recommended).
 *
 * A string within `max` chars is returned unchanged (no ellipsis added). A
 * longer string is cut back to the last full word that fits — reserving one
 * char for the trailing ellipsis so the result never exceeds `max` — and a
 * single "…" is appended. Never cuts mid-word, never leaves a dangling space
 * or stray punctuation before the ellipsis. Falls back to a hard cut only
 * when the text has no word boundary within the limit (e.g. one long token).
 */
export function shareDescription(text: string, max = MAX_DESC): string {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (normalized.length <= max) return normalized
  const ELLIPSIS = '…'
  let cut = normalized.slice(0, Math.max(0, max - ELLIPSIS.length))
  const lastSpace = cut.lastIndexOf(' ')
  if (lastSpace > 0) cut = cut.slice(0, lastSpace)
  cut = cut.replace(/[\s.,;:!?]+$/, '')
  return `${cut}${ELLIPSIS}`
}

/** Build a listing share summary: price, beds/baths/sqft, address, CTA. */
export function listingShareSummary(opts: {
  price?: number | null
  beds?: number | null
  baths?: number | null
  sqft?: number | null
  address?: string | null
  city?: string | null
  /**
   * The word an off-market listing leads with — "Sold", "Off market" — from
   * publishListingStatusWord. It sits in front of the price rather than after
   * it because this string is read as a SERP snippet and a share card, where
   * the first two words are the whole message: "$1,250,000 · 3 bed" on a home
   * that sold for $1,100,000 was the SITE-20 defect, and a corrected price
   * with no status word still reads as a home you can buy today.
   */
  statusWord?: string | null
  /**
   * The lot, already worded ("0.46-acre lot"). The caller decides when it
   * belongs: a land listing has no beds, baths or sq ft, so the lot is the one
   * size fact it has.
   */
  lot?: string | null
  /**
   * The MLS number, printed last. For a listing whose street line carries no
   * house number ("Kouns Drive", a bare parcel), which is the one key left
   * that tells it from the parcels beside it.
   */
  mlsNumber?: string | null
}): string {
  const parts: string[] = []
  const statusWord = opts.statusWord?.trim() || null
  if (opts.price != null && opts.price > 0) {
    const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(opts.price)
    parts.push(statusWord ? `${statusWord} ${money}` : money)
  } else if (statusWord) {
    parts.push(statusWord)
  }
  if (opts.beds != null || opts.baths != null) {
    const b = [opts.beds != null ? `${opts.beds} bed` : null, opts.baths != null ? `${opts.baths} bath` : null].filter(Boolean).join(', ')
    if (b) parts.push(b)
  }
  if (opts.sqft != null && opts.sqft > 0) {
    parts.push(`${Number(opts.sqft).toLocaleString()} sq ft`)
  }
  const lot = opts.lot?.trim()
  if (lot) parts.push(lot)
  const location = [opts.address, opts.city].filter(Boolean).join(opts.address && opts.city ? ', ' : '')
  if (location) parts.push(location)
  const mlsNumber = opts.mlsNumber?.trim()
  if (mlsNumber) parts.push(`MLS ${mlsNumber}`)
  const raw = parts.join(' · ')
  return shareDescription(raw || 'Property for sale. Ryan Realty')
}

/** Max length for social post text (Twitter allows 280; URL takes ~23, so leave room). */
const MAX_SHARE_TEXT = 250

/**
 * Build rich share text for a listing: exciting line + summary (beds, baths, sqft, price) + hashtags.
 * Used as the pre-filled message when sharing to X/Twitter, email, and native share.
 */
export function listingShareText(opts: {
  price?: number | null
  beds?: number | null
  baths?: number | null
  sqft?: number | null
  address?: string | null
  city?: string | null
  /** First sentence or excerpt from listing description for an exciting hook. */
  publicRemarks?: string | null
}): string {
  const parts: string[] = []
  const remark = (opts.publicRemarks ?? '').trim().replace(/\s+/g, ' ').slice(0, 120)
  if (remark) {
    const oneLiner = remark.includes('.') ? remark.split('.')[0].trim() + '.' : remark
    parts.push(oneLiner)
  } else {
    const stats: string[] = []
    if (opts.beds != null) stats.push(`${opts.beds} bed`)
    if (opts.baths != null) stats.push(`${opts.baths} bath`)
    if (opts.sqft != null && opts.sqft > 0) stats.push(`${Number(opts.sqft).toLocaleString()} sq ft`)
    const loc = [opts.address, opts.city].filter(Boolean).join(', ')
    if (stats.length) parts.push(`${stats.join(' · ')}${loc ? ` in ${loc}` : ''}.`)
    else if (loc) parts.push(`Beautiful property in ${loc}.`)
    else parts.push('Don\'t miss this one!')
  }
  const priceStr =
    opts.price != null && opts.price > 0
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(opts.price)
      : ''
  const summaryBits: string[] = []
  if (opts.beds != null || opts.baths != null) {
    const b = [opts.beds != null ? `${opts.beds} bed` : null, opts.baths != null ? `${opts.baths} bath` : null].filter(Boolean).join(', ')
    if (b) summaryBits.push(b)
  }
  if (opts.sqft != null && opts.sqft > 0) summaryBits.push(`${Number(opts.sqft).toLocaleString()} sq ft`)
  if (priceStr) summaryBits.push(priceStr)
  const location = [opts.address, opts.city].filter(Boolean).join(', ')
  if (location) summaryBits.push(location)
  if (summaryBits.length) parts.push(summaryBits.join(' · '))
  parts.push('Your next home awaits!')
  const hashtags = [DEFAULT_HASHTAGS, listingHashtags(opts.city)].join(' ').replace(/\s+/g, ' ').trim()
  parts.push(hashtags)
  const raw = parts.join(' ')
  return shareDescription(raw, MAX_SHARE_TEXT)
}

/** Standard OG image dimensions (1200×630 recommended for all platforms). */
export const OG_IMAGE_WIDTH = 1200
export const OG_IMAGE_HEIGHT = 630
